import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { plansService, type PlanItem, type PlanItemPatch, type PlanSettings, type WeeklyPlan } from '@/shared/services/plans.service';

export const PLAN_KEY = 'weekly-plan';
/** How often a plan that is being made is asked about again. */
const GENERATING_POLL_MS = 4000;

/** The brand's latest weekly plan (Task 2.3); asked about every few seconds while it is being made. */
export function useCurrentPlan() {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [PLAN_KEY, 'current', wsId],
    queryFn: () => plansService.current(),
    enabled: !!wsId,
    staleTime: 30_000,
    refetchInterval: (query) => (query.state.data?.plan?.status === 'generating' ? GENERATING_POLL_MS : false),
  });
}

/** The current plan's open items: the rail badge and the "Schedule all" count. */
export function openItems(plan: WeeklyPlan | null | undefined): PlanItem[] {
  return (plan?.items ?? []).filter((i) => i.status === 'proposed' || i.status === 'edited');
}

/** Puts a changed item into the cached plan, so the screen moves at once. */
function useItemWriter(): (item: PlanItem) => void {
  const qc = useQueryClient();
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return (item) => {
    qc.setQueryData<{ enabled: boolean; plan: WeeklyPlan | null }>([PLAN_KEY, 'current', wsId], (old) => {
      if (!old?.plan) return old;
      const exists = old.plan.items.some((i) => i.id === item.id);
      return { ...old, plan: { ...old.plan, items: exists ? old.plan.items.map((i) => (i.id === item.id ? item : i)) : [...old.plan.items, item] } };
    });
  };
}

export function usePlanActions() {
  const qc = useQueryClient();
  const write = useItemWriter();
  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: [PLAN_KEY] });
    // Accepting and undoing make or remove posts.
    void qc.invalidateQueries({ queryKey: ['posts'] });
  };
  return {
    item: useMutation({
      mutationFn: ({ planId, itemId, action }: { planId: string; itemId: string; action: 'accept' | 'undo' | 'skip' | 'regenerate' }) => plansService.itemAction(planId, itemId, action),
      onSuccess: (item) => { write(item); refresh(); },
    }),
    patch: useMutation({
      mutationFn: ({ planId, itemId, patch }: { planId: string; itemId: string; patch: PlanItemPatch }) => plansService.patchItem(planId, itemId, patch),
      onSuccess: (item) => write(item),
    }),
    acceptAll: useMutation({ mutationFn: (planId: string) => plansService.acceptAll(planId), onSuccess: refresh }),
    generate: useMutation({ mutationFn: () => plansService.generate(), onSuccess: refresh }),
    regenerate: useMutation({ mutationFn: (planId: string) => plansService.regenerate(planId), onSuccess: refresh }),
    addFromTopic: useMutation({
      mutationFn: ({ planId, topicId }: { planId: string; topicId: string }) => plansService.addFromTopic(planId, topicId),
      onSuccess: (item) => { write(item); refresh(); },
    }),
  };
}

export function usePlanSettings(enabled = true) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({ queryKey: [PLAN_KEY, 'settings', wsId], queryFn: () => plansService.settings(), enabled: enabled && !!wsId });
}

export function useSavePlanSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<PlanSettings>) => plansService.saveSettings(patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [PLAN_KEY, 'settings'] }),
  });
}
